<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AgentSelector from '../teams/AgentSelector.vue';
import StageForm from './StageForm.vue';
import StageAutomationForm from './StageAutomationForm.vue';

const route = useRoute();
const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const kanbanId = computed(() => Number(route.params.kanbanId));

const kanban = computed(() =>
  getters['kanbans/getKanban'].value(kanbanId.value)
);
const stages = computed(() =>
  getters['kanbans/getStagesByKanbanId'].value(kanbanId.value)
);
const agentList = computed(() => getters['agents/getAgents'].value);
const kanbanMembers = computed(() =>
  getters['kanbanMembers/getKanbanMembers'].value(kanbanId.value)
);
const memberUiFlags = computed(() => getters['kanbanMembers/getUIFlags'].value);

const orderedStages = ref([]);
const showStageModal = ref(false);
const selectedStage = ref({});
const showDeleteStagePopup = ref(false);
const showAutomationModal = ref(false);
const automationStage = ref({});
const selectedAgents = ref([]);
const isSavingMembers = ref(false);

const syncOrderedStages = () => {
  orderedStages.value = [...stages.value];
};

const openAddStage = () => {
  selectedStage.value = {};
  showStageModal.value = true;
};
const openEditStage = stage => {
  selectedStage.value = stage;
  showStageModal.value = true;
};
const hideStageModal = () => {
  showStageModal.value = false;
};

const openStageAutomation = stage => {
  automationStage.value = stage;
  showAutomationModal.value = true;
};
const hideAutomationModal = () => {
  showAutomationModal.value = false;
};

const openDeleteStage = stage => {
  selectedStage.value = stage;
  showDeleteStagePopup.value = true;
};
const closeDeleteStage = () => {
  showDeleteStagePopup.value = false;
};
const confirmDeleteStage = async () => {
  closeDeleteStage();
  try {
    await store.dispatch('kanbans/deleteStage', {
      kanbanId: kanbanId.value,
      id: selectedStage.value.id,
    });
    useAlert(t('KANBAN_SETTINGS.STAGE.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('KANBAN_SETTINGS.STAGE.DELETE.ERROR_MESSAGE'));
  }
};

const onStageReorder = async () => {
  await Promise.all(
    orderedStages.value.map((stage, index) =>
      store.dispatch('kanbans/updateStage', {
        kanbanId: kanbanId.value,
        id: stage.id,
        position: index + 1,
      })
    )
  );
};

const updateSelectedAgents = newList => {
  selectedAgents.value = [...newList];
};

const saveMembers = async () => {
  isSavingMembers.value = true;
  try {
    await store.dispatch('kanbanMembers/update', {
      kanbanId: kanbanId.value,
      agentsList: selectedAgents.value,
    });
    useAlert(t('KANBAN_SETTINGS.MEMBERS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('KANBAN_SETTINGS.MEMBERS.API.ERROR_MESSAGE'));
  } finally {
    isSavingMembers.value = false;
  }
};

onMounted(async () => {
  store.dispatch('agents/get');
  if (!kanban.value?.id) {
    await store.dispatch('kanbans/get');
  }
  syncOrderedStages();
  try {
    await store.dispatch('kanbanMembers/get', { kanbanId: kanbanId.value });
    updateSelectedAgents(kanbanMembers.value.map(item => item.id));
  } catch (error) {
    updateSelectedAgents([]);
  }
});
</script>

<template>
  <div class="w-full h-full max-w-5xl px-1 py-6 mx-auto overflow-auto">
    <header class="mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12">
        {{ kanban?.name }}
      </h1>
      <p v-if="kanban?.description" class="mt-1 text-sm text-n-slate-11">
        {{ kanban?.description }}
      </p>
    </header>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <!-- Left column: Stages -->
      <section class="lg:col-span-7">
        <div class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
          <div
            class="flex items-start justify-between gap-3 pb-4 mb-4 border-b border-n-weak"
          >
            <div class="min-w-0">
              <h2
                class="flex items-center gap-2 text-base font-bold text-n-slate-12"
              >
                <Icon
                  icon="i-lucide-kanban-square"
                  class="text-n-ruby-9 size-4"
                />
                {{ t('KANBAN_SETTINGS.STAGE.SECTION_TITLE') }}
              </h2>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ t('KANBAN_SETTINGS.STAGE.SECTION_DESC') }}
              </p>
            </div>
            <NextButton
              ghost
              sm
              icon="i-lucide-plus"
              :label="t('SIDEBAR.NEW_KANBAN_STAGE')"
              class="shrink-0"
              @click="openAddStage"
            />
          </div>

          <Draggable
            v-model="orderedStages"
            item-key="id"
            class="space-y-3"
            handle=".drag-handle"
            ghost-class="kanban-stage-ghost"
            @end="onStageReorder"
          >
            <template #item="{ element: stage }">
              <div
                class="flex items-center justify-between p-4 transition-colors border rounded-lg group border-n-weak bg-n-background hover:border-n-ruby-8/40"
              >
                <div class="flex items-center min-w-0 gap-3">
                  <Icon
                    icon="i-lucide-grip-vertical"
                    class="cursor-grab drag-handle text-n-slate-9 size-4 shrink-0"
                  />
                  <span
                    class="rounded-full size-2.5 shrink-0"
                    :style="{ backgroundColor: stage.color }"
                  />
                  <span class="text-sm font-semibold truncate text-n-slate-12">
                    {{ stage.name }}
                  </span>
                </div>
                <div
                  class="flex items-center gap-1 transition-opacity opacity-0 shrink-0 group-hover:opacity-100"
                >
                  <NextButton
                    v-tooltip.top="t('KANBAN_SETTINGS.AUTOMATION.TOOLTIP')"
                    icon="i-lucide-zap"
                    slate
                    xs
                    ghost
                    @click="openStageAutomation(stage)"
                  />
                  <NextButton
                    icon="i-woot-edit-pen"
                    slate
                    xs
                    ghost
                    @click="openEditStage(stage)"
                  />
                  <NextButton
                    icon="i-woot-bin"
                    slate
                    xs
                    ghost
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDeleteStage(stage)"
                  />
                </div>
              </div>
            </template>
          </Draggable>

          <p
            v-if="!orderedStages.length"
            class="py-6 text-sm text-center text-n-slate-11"
          >
            {{ t('KANBAN.NO_STAGES') }}
          </p>
        </div>
      </section>

      <!-- Right column: Access -->
      <section class="lg:col-span-5">
        <div
          class="flex flex-col h-full overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="px-8 py-5 border-b border-n-weak shrink-0">
            <h2
              class="flex items-center gap-2 text-base font-bold text-n-slate-12"
            >
              <Icon icon="i-lucide-shield-check" class="text-n-ruby-9 size-4" />
              {{ t('KANBAN_SETTINGS.MEMBERS.SECTION_TITLE') }}
            </h2>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('KANBAN_SETTINGS.MEMBERS.SECTION_DESC') }}
            </p>
          </div>
          <form
            v-if="agentList.length"
            class="flex flex-col flex-1 min-h-0 px-8 py-5"
            @submit.prevent="saveMembers"
          >
            <AgentSelector
              :agent-list="agentList"
              :selected-agents="selectedAgents"
              :update-selected-agents="updateSelectedAgents"
              :is-working="isSavingMembers || memberUiFlags.isUpdating"
              :submit-button-text="t('KANBAN_SETTINGS.MEMBERS.SAVE')"
            />
          </form>
          <div v-else class="flex items-center justify-center py-10">
            <Spinner class="text-n-blue-11" />
          </div>
        </div>
      </section>
    </div>
  </div>

  <woot-modal v-model:show="showStageModal" :on-close="hideStageModal">
    <StageForm
      :kanban-id="kanbanId"
      :selected-stage="selectedStage"
      @close="hideStageModal"
    />
  </woot-modal>

  <woot-modal
    v-model:show="showAutomationModal"
    :on-close="hideAutomationModal"
  >
    <StageAutomationForm
      v-if="showAutomationModal"
      :kanban-id="kanbanId"
      :stage="automationStage"
      @close="hideAutomationModal"
    />
  </woot-modal>

  <woot-delete-modal
    v-model:show="showDeleteStagePopup"
    :on-close="closeDeleteStage"
    :on-confirm="confirmDeleteStage"
    :title="t('KANBAN_SETTINGS.STAGE.DELETE.CONFIRM_TITLE')"
    :message="t('KANBAN_SETTINGS.STAGE.DELETE.CONFIRM_MESSAGE')"
    :message-value="` ${selectedStage.name}?`"
    :confirm-text="t('KANBAN_SETTINGS.DELETE.CONFIRM.YES')"
    :reject-text="t('KANBAN_SETTINGS.DELETE.CONFIRM.NO')"
  />
</template>

<style scoped lang="scss">
.kanban-stage-ghost {
  @apply opacity-50 bg-n-slate-3 dark:bg-n-solid-2;
}
</style>
